import React, { useState, useEffect } from "react";
import { Dialog } from "primereact/dialog";
import { MultiSelect } from "primereact/multiselect";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Fetch } from "../../shared/Fetch";
import { ICompanyMastDropdown } from "../../models/IDropdown";
import { IPrimeVendorAllocation } from "./Table";

interface IProps {
  open: boolean;
  onHide: () => void;
  rowData: IPrimeVendorAllocation | null;
  toast: any;
  reload: boolean;
  setReload: (is: boolean) => void;
}

const AllocationDialog = (props: IProps) => {
  const [companyList, setCompanyList] = useState<Array<ICompanyMastDropdown>>([]);
  const [selectedNew, setSelectedNew] = useState<Array<number>>([]);
  const [allocations, setAllocations] = useState<Array<{ company_ID: number; company_Name: string }>>([]);
  const [saving, setSaving] = useState(false);

  const loadCompanies = async () => {
    const res = await Fetch("Dropdown/company", "GET", null, null, null);
    if (res) setCompanyList(await res.json());
  };

  useEffect(() => {
    if (props.open && props.rowData) {
      loadCompanies();
      setAllocations(props.rowData.allocated_Companies);
      setSelectedNew([]);
    }
  }, [props.open, props.rowData]);

  const availableCompanies = companyList.filter((c) => !allocations.some((a) => a.company_ID === c.company_ID));

  const handleAdd = async () => {
    if (!selectedNew.length) return;
    setSaving(true);
    try {
      const res = await Fetch("PV_Allocation_Mast/addallocation", "POST", { PV_Al_ID: props.rowData?.id, Company_IDs: selectedNew }, null, props.toast);
      if (res && res.ok) {
        props.toast.current?.show({ severity: "success", summary: "Added", life: 2000 });
        props.setReload(!props.reload);
        const newItems = companyList.filter((c) => selectedNew.includes(c.company_ID)).map((c) => ({ company_ID: c.company_ID, company_Name: c.company_Name }));
        setAllocations((prev) => [...prev, ...newItems]);
        setSelectedNew([]);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (company_ID: number) => {
    try {
      const res = await Fetch(`PV_Allocation_Mast/deleteallocation/${props.rowData?.id}/${company_ID}`, "DELETE", null, null, props.toast);
      if (res && res.ok) {
        props.toast.current?.show({ severity: "success", summary: "Deleted", life: 2000 });
        props.setReload(!props.reload);
        setAllocations((prev) => prev.filter((a) => a.company_ID !== company_ID));
      }
    } catch (ex: any) {
      props.toast.current?.show({ severity: "error", summary: "Error", detail: ex.message, life: 3000 });
    }
  };

  const deleteBody = (row: { company_ID: number; company_Name: string }) => <Button icon="pi pi-trash" className="p-button-danger p-button-sm p-button-text" onClick={() => handleDelete(row.company_ID)} />;

  return (
    <Dialog visible={props.open} onHide={props.onHide} header={`Allocated Companies — ${props.rowData?.from_Company_Name}`} style={{ width: "45vw" }} modal>
      <div className="flex gap-2 align-items-end mb-3">
        <div className="flex-1">
          <label className="block mb-1 font-medium">
            Add Companies
            {availableCompanies.length === 0 && <span style={{ color: "gray", fontWeight: "normal", marginLeft: "8px", fontSize: "0.8rem" }}>(All companies already allocated)</span>}
          </label>
          <MultiSelect value={selectedNew} onChange={(e) => setSelectedNew(e.value)} options={availableCompanies} optionValue="company_ID" optionLabel="company_Name" display="chip" filter placeholder="Select companies to add" className="w-full" disabled={availableCompanies.length === 0} />
        </div>
        <Button label="ADD" icon="pi pi-plus" className="p-button-success" onClick={handleAdd} loading={saving} disabled={!selectedNew.length} />
      </div>

      <DataTable value={allocations} size="small" stripedRows showGridlines emptyMessage="No companies allocated.">
        <Column field="company_Name" header="Company Name" />
        <Column body={deleteBody} header="Remove" style={{ width: "80px", textAlign: "center" }} />
      </DataTable>
    </Dialog>
  );
};

export default AllocationDialog;
